import {
  Truck, Calendar, Clock, CheckCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  DeliveryCard, DeliveryDetailModal 
} from '@/features/receiving';
import type {
  Delivery, DeliveryStatus 
} from '@/features/receiving';
import {
  STATUS_TABS, type TabId 
} from './receiving-constants';

interface DeliveriesViewProps {
  readonly activeTab: TabId;
  readonly deliveries: Delivery[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly stats: {
    total: number;
    scheduled: number;
    inProgress: number;
    completed: number;
    partial: number;
  };
  readonly selectedStatus: DeliveryStatus | undefined;
  readonly setSelectedStatus: (status: DeliveryStatus | undefined) => void;
  readonly selectedDelivery: Delivery | null;
  readonly onSelectDelivery: (delivery: Delivery | null) => void;
  readonly onBulkUpload: (file: File) => void;
  readonly onRefetch: () => void;
  readonly onReportDiscrepancy: (delivery: Delivery) => void;
  readonly onStartReceiving?: (deliveryId: string) => void;
  readonly onMarkComplete?: (deliveryId: string) => void;
}

export function DeliveriesView({
  activeTab,
  deliveries,
  isLoading,
  error,
  stats,
  selectedStatus,
  setSelectedStatus,
  selectedDelivery,
  onSelectDelivery,
  onBulkUpload,
  onRefetch,
  onReportDiscrepancy,
  onStartReceiving,
  onMarkComplete,
}: DeliveriesViewProps) {
  const exportData = deliveries.map((d) => ({
    deliveryId: d.deliveryId,
    poNumber: d.poNumber,
    supplier: d.supplierName,
    status: d.status,
    scheduledDate: d.scheduledDate,
    totalLines: d.totalLines,
    receivedLines: d.receivedLines,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Delivery"
        entityNamePlural="Deliveries"
        onBulkUpload={onBulkUpload}
        exportData={exportData}
        exportFilename="deliveries.csv"
        canAdd={false}
        canEdit={false}
        canDelete={false}
      />

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard title="Total Deliveries" value={stats.total} icon={Truck} />
        <StatCard title="Scheduled" value={stats.scheduled} icon={Calendar} color="blue" />
        <StatCard title="In Progress" value={stats.inProgress} icon={Clock} color="amber" />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="green" />
      </div>

      {activeTab !== 'expected' && (
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((tab) => (
            <Button
              key={tab.label}
              variant={selectedStatus === tab.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedStatus(tab.value)}
              className={selectedStatus === tab.value ? 'bg-slate-900 hover:bg-slate-800' : 'border-slate-200'}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-slate-100" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Truck className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium text-slate-900">Failed to load deliveries</p>
            <Button variant="outline" className="mt-4" onClick={onRefetch}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && deliveries.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Truck className="mb-4 h-12 w-12 text-slate-300" />
            <p className="text-lg font-medium text-slate-900">No deliveries found</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && deliveries.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {deliveries.map((delivery) => (
            <div
              key={delivery.deliveryId}
              className="cursor-pointer"
              onClick={() => onSelectDelivery(delivery)}
            >
              <DeliveryCard delivery={delivery} />
            </div>
          ))}
        </div>
      )}

      <DeliveryDetailModal
        delivery={selectedDelivery}
        isOpen={selectedDelivery !== null}
        onClose={() => onSelectDelivery(null)}
        onStartReceiving={onStartReceiving}
        onMarkComplete={onMarkComplete}
        onReportIssue={onReportDiscrepancy}
      />
    </>
  );
}

interface StatCardProps {
  readonly title: string;
  readonly value: number;
  readonly icon: typeof Truck;
  readonly color?: 'blue' | 'amber' | 'green';
}

function StatCard({
  title, value, icon: iconComponent, color 
}: StatCardProps) {
  const colorClasses = {
    blue: 'text-blue-500',
    amber: 'text-amber-500',
    green: 'text-emerald-500',
  };

  const IconToRender = iconComponent;

  return (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-slate-500">{title}</CardTitle>
        <IconToRender className={`h-5 w-5 ${color ? colorClasses[color] : 'text-slate-400'}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${color ? colorClasses[color].replace('500', '600') : 'text-slate-900'}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
